/* ==========================================================================
   <mega-menu>  — custom, merchant-editable mega menu
   --------------------------------------------------------------------------
   The section renders a hidden <mega-menu> host containing .mega-menu__panel.
   On connect this element:
     • finds the top-level nav item whose title matches data-trigger,
     • mounts the panel as the last child of that item's <li> (so keyboard tab
       order flows link -> panel -> next item) and positions it position:fixed
       (escapes the header's horizontal-scroll overflow and stays under the
       sticky header),
     • opens on hover (pointer) and focus/keyboard, closes on mouse-leave,
       Escape, focus-out and outside click,
     • builds a collapsed <details> accordion inside the mobile menu drawer,
     • re-covers the theme's sticky-header clone as it is created.
   Conventions follow the theme's other components (guarded class + defer load).
   ========================================================================== */

if (typeof MegaMenu !== 'function') {
  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.triggerTitle = this.normalize(this.dataset.trigger || '');
      this.panel = this.querySelector('.mega-menu__panel');
      if (!this.triggerTitle || !this.panel) return;

      this.panelId = this.panel.id;
      this.desktopMQ = window.matchMedia('(min-width: 768px)');
      this.isOpen = false;
      this.activeTrigger = null;
      this.closeTimer = null;
      this.boundTriggers = new Set();

      // Bind handlers once so they can be removed on disconnect.
      this._reposition = this.reposition.bind(this);
      this._onKeyDown = this.onKeyDown.bind(this);
      this._onDocFocusIn = this.onDocFocusIn.bind(this);
      this._onDocPointerDown = this.onDocPointerDown.bind(this);

      this.mountPanel();
      this.wirePanel();
      this.refreshTriggers();
      this.buildMobile();

      document.addEventListener('keydown', this._onKeyDown);
      document.addEventListener('focusin', this._onDocFocusIn);
      document.addEventListener('pointerdown', this._onDocPointerDown, true);

      // The sticky header is a clone built by the theme after load — re-cover it.
      this._observer = new MutationObserver(() => {
        this.cleanStickyClone();
        this.refreshTriggers();
      });
      this._observer.observe(document.body, { childList: true });
      window.addEventListener('load', () => { this.cleanStickyClone(); this.refreshTriggers(); });

      this.setupEditor();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('focusin', this._onDocFocusIn);
      document.removeEventListener('pointerdown', this._onDocPointerDown, true);
      window.removeEventListener('scroll', this._reposition);
      window.removeEventListener('resize', this._reposition);
      if (this._observer) this._observer.disconnect();
      this.boundTriggers.forEach((a) => this.unbindTrigger(a));
      if (this.panel && this.panel.parentNode) this.panel.remove();
      this.removeMobile();
    }

    /* ---- helpers -------------------------------------------------------- */

    normalize(str) {
      return (str || '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    // All top-level nav links (original header + sticky clone) matching the title.
    getTriggers() {
      const links = document.querySelectorAll('.site-nav.style--classic .link-list > li > a');
      return Array.from(links).filter((a) => this.normalize(a.textContent) === this.triggerTitle);
    }

    /* ---- panel mounting ------------------------------------------------- */

    mountPanel() {
      const primary = this.getTriggers().find((a) => !a.closest('.sticky-header'));
      if (primary && primary.closest('li')) {
        primary.closest('li').appendChild(this.panel); // keeps tab order natural
        this.panelMounted = true;
      } else {
        // No matching menu item yet: park at body so it still works / is editable.
        document.body.appendChild(this.panel);
        this.panelMounted = false;
        if (window.console) console.warn(`[mega-menu] No top-level menu item titled "${this.dataset.trigger}" was found. Set "Opens under this menu item" to an exact menu item title.`);
      }
    }

    wirePanel() {
      this.panel.addEventListener('pointerenter', () => this.cancelClose());
      this.panel.addEventListener('pointerleave', () => this.scheduleClose());
    }

    /* ---- trigger binding ------------------------------------------------ */

    refreshTriggers() {
      this.getTriggers().forEach((a) => this.bindTrigger(a));
    }

    bindTrigger(a) {
      if (this.boundTriggers.has(a)) return;
      this.boundTriggers.add(a);

      const li = a.closest('li');
      if (li) li.classList.add('mega-trigger');
      a.setAttribute('aria-haspopup', 'true');
      a.setAttribute('aria-expanded', 'false');
      a.setAttribute('aria-controls', this.panelId);

      a._megaEnter = () => this.open(a);
      a._megaLeave = () => this.scheduleClose();
      a._megaFocus = () => this.open(a);
      a.addEventListener('pointerenter', a._megaEnter);
      a.addEventListener('pointerleave', a._megaLeave);
      a.addEventListener('focus', a._megaFocus);
      if (li) {
        li._megaLeave = () => this.scheduleClose();
        li.addEventListener('pointerleave', li._megaLeave);
      }
    }

    unbindTrigger(a) {
      const li = a.closest('li');
      if (li) li.classList.remove('mega-trigger');
      a.removeAttribute('aria-haspopup');
      a.removeAttribute('aria-expanded');
      a.removeAttribute('aria-controls');
      if (a._megaEnter) a.removeEventListener('pointerenter', a._megaEnter);
      if (a._megaLeave) a.removeEventListener('pointerleave', a._megaLeave);
      if (a._megaFocus) a.removeEventListener('focus', a._megaFocus);
      if (li && li._megaLeave) li.removeEventListener('pointerleave', li._megaLeave);
      this.boundTriggers.delete(a);
    }

    // The theme clones the header into .sticky-header (ids suffixed -sticky).
    // Drop any cloned dead panel and point the clone's trigger at the real one.
    cleanStickyClone() {
      document.querySelectorAll('.sticky-header .mega-menu__panel').forEach((p) => p.remove());
      document.querySelectorAll('.sticky-header .site-nav.style--classic .link-list > li > a').forEach((a) => {
        if (this.normalize(a.textContent) === this.triggerTitle) {
          a.setAttribute('aria-controls', this.panelId);
        }
      });
    }

    /* ---- open / close --------------------------------------------------- */

    open(trigger) {
      if (!this.desktopMQ.matches) return; // desktop panel only; mobile uses the drawer
      this.cancelClose();
      this.activeTrigger = trigger;
      this.reposition();
      if (!this.isOpen) {
        this.panel.classList.add('is-open');
        this.isOpen = true;
        window.addEventListener('scroll', this._reposition, { passive: true });
        window.addEventListener('resize', this._reposition);
      }
      this.getTriggers().forEach((t) => t.setAttribute('aria-expanded', String(t === trigger)));
    }

    close() {
      this.cancelClose();
      if (!this.isOpen) return;
      this.panel.classList.remove('is-open');
      this.isOpen = false;
      this.activeTrigger = null;
      this.getTriggers().forEach((t) => t.setAttribute('aria-expanded', 'false'));
      window.removeEventListener('scroll', this._reposition);
      window.removeEventListener('resize', this._reposition);
    }

    scheduleClose() {
      if (this.editorLocked) return;
      this.cancelClose();
      this.closeTimer = window.setTimeout(() => this.close(), 220);
    }

    cancelClose() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    reposition() {
      const t = this.activeTrigger || this.getTriggers()[0];
      if (!t) return;
      const header = t.closest('.sticky-header') || t.closest('main-header') || t.closest('.site-header-container') || t.closest('header');
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      const top = Math.max(0, Math.round(bottom));
      this.panel.style.top = top + 'px';
      this.panel.style.maxHeight = (window.innerHeight - top) + 'px';
      this.panel.style.overflowY = 'auto';
    }

    /* ---- keyboard / dismissal ------------------------------------------ */

    onKeyDown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        const back = this.activeTrigger;
        this.close();
        if (back) back.focus();
      }
    }

    onDocFocusIn(e) {
      if (!this.isOpen) return;
      if (this.panel.contains(e.target)) return;
      const onTrigger = Array.from(this.boundTriggers).some((a) => a.closest('li') && a.closest('li').contains(e.target));
      if (!onTrigger) this.close();
    }

    onDocPointerDown(e) {
      if (!this.isOpen) return;
      if (this.panel.contains(e.target)) return;
      const onTrigger = Array.from(this.boundTriggers).some((a) => a.closest('li') && a.closest('li').contains(e.target));
      if (!onTrigger) this.close();
    }

    /* ---- mobile accordion (folded into the drawer) --------------------- */

    buildMobile() {
      this.removeMobile();
      const drawer = document.getElementById('site-menu-sidebar');
      const list = drawer && drawer.querySelector('.site-nav.style--sidebar .link-list');
      if (!list) return;

      const wrap = document.createElement('li');
      wrap.className = 'mega-menu__mobile-wrap';
      wrap.setAttribute('data-mega-mobile', this.dataset.sectionId || '');
      const ul = document.createElement('ul');
      ul.className = 'mega-menu__mobile';

      this.panel.querySelectorAll('.mega-menu__column').forEach((col) => {
        // Image columns are desktop-only — never render them in the mobile menu.
        if (col.classList.contains('mega-menu__column--image')) return;
        this.buildMobileTextGroups(col).forEach((el) => ul.appendChild(el));
      });

      if (!ul.children.length) return;
      wrap.appendChild(ul);

      const triggerLi = this.findSidebarTrigger(list);
      if (triggerLi && triggerLi.parentNode === list) triggerLi.after(wrap);
      else list.appendChild(wrap);
      this.mobileEl = wrap;
    }

    buildMobileTextGroups(col) {
      const groups = [];
      let details = null;
      let linksUl = null;
      const flush = () => {
        if (details && linksUl && linksUl.children.length) {
          details.appendChild(linksUl);
          groups.push(details);
        }
        details = null;
        linksUl = null;
      };
      const startGroup = (label, open) => {
        flush();
        details = document.createElement('details');
        details.className = 'mega-menu__m-group';
        if (open) details.open = true;
        const summary = document.createElement('summary');
        summary.className = 'mega-menu__m-summary';
        summary.textContent = label;
        details.appendChild(summary);
        linksUl = document.createElement('ul');
        linksUl.className = 'mega-menu__m-links';
      };

      Array.from(col.children).forEach((child) => {
        if (child.classList.contains('mega-menu__heading')) {
          startGroup(child.textContent.trim(), false);
        } else if (child.classList.contains('mega-menu__link')) {
          if (!details) startGroup(this.dataset.trigger || 'Menu', true); // links before any heading
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'mega-menu__m-link';
          const href = child.getAttribute('href');
          if (href) a.setAttribute('href', href);
          a.textContent = child.textContent.trim();
          li.appendChild(a);
          linksUl.appendChild(li);
        }
      });
      flush();
      return groups;
    }

    findSidebarTrigger(list) {
      const anchors = list.querySelectorAll(':scope > li > a');
      for (const a of anchors) {
        if (this.normalize(a.textContent) === this.triggerTitle) return a.closest('li');
      }
      return null;
    }

    removeMobile() {
      const id = this.dataset.sectionId || '';
      document.querySelectorAll('[data-mega-mobile]').forEach((el) => {
        if (!id || el.getAttribute('data-mega-mobile') === id) el.remove();
      });
      this.mobileEl = null;
    }

    /* ---- theme editor --------------------------------------------------- */

    setupEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      const mine = (e) => this.panel.contains(e.target) || this.contains(e.target) ||
        (e.detail && String(e.detail.sectionId) === String(this.dataset.sectionId));

      document.addEventListener('shopify:block:select', (e) => { if (mine(e)) this.editorOpen(); });
      document.addEventListener('shopify:block:deselect', () => this.editorClose());
      document.addEventListener('shopify:section:select', (e) => { if (mine(e)) this.editorOpen(); });
      document.addEventListener('shopify:section:deselect', (e) => { if (mine(e)) this.editorClose(); });
    }

    editorOpen() {
      this.editorLocked = true;
      const trigger = this.getTriggers()[0];
      if (trigger) {
        this.open(trigger);
      } else {
        // No trigger to anchor to — show it fixed under the header so it's editable.
        this.reposition();
        this.panel.classList.add('is-open');
        this.isOpen = true;
      }
    }

    editorClose() {
      this.editorLocked = false;
      this.close();
    }
  }

  customElements.define('mega-menu', MegaMenu);
}
